import { useEffect, useRef, useState } from 'react';
import { api, EchecApi } from '../partage/api/client';
import type { ReponseDemandeDAutorisation } from '../partage/api/types';
import {
  AUTORISER,
  CE_QUE_CLAUDE_FERA,
  CE_QUE_CLAUDE_POURRA_FAIRE,
  CLAUDE_VEUT_ACCEDER,
  DEMANDE_QUI_NE_VAUT_PLUS,
  GESTE_SANS_RESEAU,
  JAMAIS_SUPPRIMER,
  ON_COUPE_DANS_PARAMETRES,
  PAS_DE_REPONSE,
  RECOMMENCE_DEPUIS_CLAUDE,
  REESSAYER,
  REFUSER,
  RETOUR_LOCAL,
  UN_INSTANT,
  bonjour,
  tuReviendrasSur,
} from '../partage/vocabulaire';
import { AccueilSansCompte } from './AccueilSansCompte';
import './Connexion.css';
import './Autoriser.css';

/** L'adresse de la page, où `GET /oauth/authorize` envoie le navigateur (`server/src/routes/oauth.ts`). */
export const CHEMIN_AUTORISER = '/autoriser';

/** L'identifiant opaque de la demande, dans l'adresse : il ne sert qu'avec la session, ce n'est pas un secret. */
function demandeDeLAdresse(): string {
  return new URLSearchParams(window.location.search).get('demande') ?? '';
}

function messageDEchec(erreur: unknown): string {
  if (erreur instanceof EchecApi && erreur.code === 'hors_ligne') return GESTE_SANS_RESEAU;
  return PAS_DE_REPONSE;
}

type Etat = { sorte: 'lecture' } | { sorte: 'prete'; demande: ReponseDemandeDAutorisation } | { sorte: 'perimee' } | { sorte: 'panne'; message: string };

/**
 * **Autoriser** (chantier connecteur-claude, ticket 02) : Claude demande l'accès au compte de la
 * session. Sur le papier de Nextilt, le prénom, ce que Claude pourra faire, l'hôte où l'on
 * reviendra, « Autoriser » et « Refuser ». La décision rend l'adresse de retour de Claude, avec le
 * code ou le refus : le navigateur y part. Sans session, l'app montre d'abord la connexion, puis
 * revient ici (`App.tsx`).
 */
export function Autoriser() {
  const [demande] = useState(demandeDeLAdresse);
  const [etat, setEtat] = useState<Etat>({ sorte: 'lecture' });
  const [enCours, setEnCours] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const parti = useRef(false);

  useEffect(() => {
    if (parti.current) return;
    parti.current = true;
    if (demande === '') {
      setEtat({ sorte: 'perimee' });
      return;
    }
    api.demandeDAutorisation(demande).then(
      (lue) => setEtat({ sorte: 'prete', demande: lue }),
      (erreur: unknown) =>
        setEtat(erreur instanceof EchecApi && erreur.code === 'demande_inconnue' ? { sorte: 'perimee' } : { sorte: 'panne', message: messageDEchec(erreur) })
    );
  }, [demande]);

  async function decider(autoriser: boolean) {
    if (enCours) return;
    setEnCours(true);
    setRefus(null);
    try {
      const { adresse } = await api.deciderLaDemande(demande, autoriser);
      window.location.assign(adresse);
    } catch (erreur) {
      if (erreur instanceof EchecApi && erreur.code === 'demande_inconnue') setEtat({ sorte: 'perimee' });
      else setRefus(messageDEchec(erreur));
      setEnCours(false);
    }
  }

  if (etat.sorte === 'lecture') return <AccueilSansCompte titre={UN_INSTANT} description="" centre>{null}</AccueilSansCompte>;
  if (etat.sorte === 'perimee') {
    return (
      <AccueilSansCompte titre={DEMANDE_QUI_NE_VAUT_PLUS} description="" centre>
        <p className="connexion-description">{RECOMMENCE_DEPUIS_CLAUDE}</p>
      </AccueilSansCompte>
    );
  }
  if (etat.sorte === 'panne') {
    return (
      <AccueilSansCompte titre={CLAUDE_VEUT_ACCEDER} description={etat.message} centre>
        <button type="button" className="connexion-entrer connexion-plein" onClick={() => window.location.reload()}>
          {REESSAYER}
        </button>
      </AccueilSansCompte>
    );
  }

  const { qui, hoteDeRetour, retourLocal } = etat.demande;
  return (
    <AccueilSansCompte titre={CLAUDE_VEUT_ACCEDER} description={bonjour(qui)}>
      <div className="autoriser">
        <p className="autoriser-intitule">{CE_QUE_CLAUDE_POURRA_FAIRE}</p>
        <ul className="autoriser-liste">
          {CE_QUE_CLAUDE_FERA.map((geste) => (
            <li key={geste}>{geste}</li>
          ))}
        </ul>
        <p className="autoriser-jamais">{JAMAIS_SUPPRIMER}</p>
        <p className="connexion-aide">
          {tuReviendrasSur(hoteDeRetour)} {ON_COUPE_DANS_PARAMETRES}
        </p>
        {retourLocal && <p className="refus connexion-refus">{RETOUR_LOCAL}</p>}
        <button type="button" className="connexion-entrer" onClick={() => void decider(true)} disabled={enCours} autoFocus>
          {enCours ? UN_INSTANT : AUTORISER}
        </button>
        <button type="button" className="connexion-second" onClick={() => void decider(false)} disabled={enCours}>
          {REFUSER}
        </button>
        {refus && (
          <p className="refus connexion-refus" role="status">
            {refus}
          </p>
        )}
      </div>
    </AccueilSansCompte>
  );
}
